import { useEffect } from "react";
import { Link } from "react-router-dom";
import { CycleIcon, ScaleIcon, TrophyIcon, ShieldIcon, TruckIcon, AtomIcon, BoltIcon, GearIcon } from "../Icons";

function WhyUsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    const reveals = document.querySelectorAll(".reveal");
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add("visible"); observer.unobserve(e.target); }
      });
    }, { threshold: 0.1 });
    reveals.forEach((r) => observer.observe(r));
    return () => observer.disconnect();
  }, []);

  return (
    <main style={{ paddingTop: "80px" }}>

      {/* PAGE HERO */}
      <section className="page-hero">
        <div className="hero-grid-bg"></div>
        <div className="hero-glow" style={{ opacity: 0.4 }}></div>
        <div className="section-inner" style={{ position: "relative", zIndex: 2 }}>
          <div className="section-tag">Why Choose Us</div>
          <h1 className="section-title display" style={{ fontSize: "clamp(3rem, 6vw, 5.5rem)", marginBottom: "1.2rem" }}>
            Built for Scale.<br />Designed for Trust.
          </h1>
          <p className="section-body" style={{ maxWidth: "620px" }}>
            India has recyclers. It doesn't have a battery circular economy infrastructure — yet. That's what we're building, and here's why it matters who you partner with.
          </p>
        </div>
      </section>

      {/* FOUR PILLARS */}
      <section>
        <div className="section-inner">
          <div className="section-tag">Our Differentiators</div>
          <h2 className="section-title display">What Sets Us Apart</h2>

          <div className="page-pillar-grid reveal">

            <div className="page-pillar-card">
              <div className="page-pillar-icon"><CycleIcon size={44} /></div>
              <h3>End-to-End Ownership</h3>
              <p>
                Most recyclers handle one part of the chain — collection, or shredding, or Black Mass sales. We own the entire flow: from your facility doorstep to the downstream refiner. That means one partner, one contract, one point of accountability, and an unbroken chain of custody for your EPR records.
              </p>
              <ul className="page-pillar-list">
                <li>Single contract covers full lifecycle</li>
                <li>Full chain-of-custody documentation</li>
                <li>No gap between collection and processing</li>
              </ul>
            </div>

            <div className="page-pillar-card">
              <div className="page-pillar-icon"><ScaleIcon size={44} /></div>
              <h3>Designed to Scale With You</h3>
              <p>
                Our facility is modular by design. We're processing 5 TPD today, with a clear engineering roadmap to 50+ TPD as India's battery waste curve steepens. If you're an OEM projecting significant volume growth over the next 3–5 years, we're the partner who will still be able to handle you.
              </p>
              <ul className="page-pillar-list">
                <li>Current capacity: 5 TPD</li>
                <li>Phase 2 target: 20 TPD</li>
                <li>Long-term roadmap: 50+ TPD</li>
              </ul>
            </div>

            <div className="page-pillar-card">
              <div className="page-pillar-icon"><TrophyIcon size={44} /></div>
              <h3>High-Grade Output, Consistently</h3>
              <p>
                Black Mass quality varies dramatically between recyclers. Our controlled separation process — inert atmosphere shredding, multi-stage classification, precise thermal treatment — consistently produces Black Mass above 85% purity, which is what premium hydromet refiners require and pay for. That margin is what makes our economics work without cutting corners.
              </p>
              <ul className="page-pillar-list">
                <li>Black Mass purity: &gt; 85% by weight</li>
                <li>Material test certificates per batch</li>
                <li>Premium pricing from downstream refiners</li>
              </ul>
            </div>

            <div className="page-pillar-card">
              <div className="page-pillar-icon"><ShieldIcon size={44} /></div>
              <h3>Compliance You Can Rely On</h3>
              <p>
                EPR compliance is not something you want uncertainty around. We are registered under India's Battery Waste Management Rules 2022, authorised under Hazardous Waste Rules, and aligned with global ESG frameworks. Every producer who partners with us receives EPR certificates that stand up to CPCB scrutiny.
              </p>
              <ul className="page-pillar-list">
                <li>EPR registered under BWMR 2022</li>
                <li>Hazardous Waste authorised</li>
                <li>ESG reporting alignment</li>
              </ul>
            </div>

          </div>
        </div>
      </section>

      {/* THE TEAM ANGLE */}
      <section style={{ background: "var(--panel)" }}>
        <div className="section-inner">
          <div className="section-tag">Our Edge</div>
          <h2 className="section-title display">Process Over Promise</h2>
          <p className="section-body">
            We don't make commitments we can't back with process documentation. Every claim below is operationally grounded.
          </p>
          <div className="safety-grid reveal" style={{ marginTop: "3rem" }}>
            {[
              { icon: <TruckIcon size={22} />, title: "Logistics Network", body: "Growing collection partnerships across Rajasthan, Delhi NCR, and Gujarat — expanding alongside our facility capacity." },
              { icon: <BoltIcon size={22} />, title: "Zero-Skip Discharge", body: "Every incoming battery is fully discharged before processing. No exceptions. This is the most common safety failure at informal recyclers." },
              { icon: <AtomIcon size={22} />, title: "Material Traceability", body: "Every input batch is logged with source, chemistry, weight, and condition. Every output is matched to an input. Fully auditable." },
              { icon: <GearIcon size={22} />, title: "Modular Capacity", body: "Facility design allows capacity additions without full-line shutdowns — minimising disruption to existing collection partners during expansion." },
            ].map((s) => (
              <div className="usp-item" key={s.title}>
                <div className="usp-icon-wrap">{s.icon}</div>
                <div>
                  <h3>{s.title}</h3>
                  <p>{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPARISON */}
      <section>
        <div className="section-inner">
          <div className="section-tag">The Alternative</div>
          <h2 className="section-title display">Formal vs. Informal</h2>
          <p className="section-body">
            The informal recycling sector exists, and it's cheaper upfront. Here's what that actually costs.
          </p>
          <div className="compare-table reveal">
            <div className="compare-header">
              <div></div>
              <div className="compare-col-header">Informal Recycler</div>
              <div className="compare-col-header alankaar">Alankaar Power</div>
            </div>
            {[
              ["EPR Certificate Issued", "✗", "✓"],
              ["Chain of Custody Docs", "✗", "✓"],
              ["Safe Discharge Process", "Often skipped", "Always performed"],
              ["Black Mass Quality", "Variable / low", "> 85% purity"],
              ["ESG / Audit Ready", "✗", "✓"],
              ["Regulatory Liability Risk", "High", "None"],
              ["Scales With Your Volume", "✗", "✓"],
            ].map(([label, informal, us]) => (
              <div className="compare-row" key={label}>
                <div className="compare-label">{label}</div>
                <div className="compare-val neg">{informal}</div>
                <div className="compare-val pos">{us}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: "var(--panel)", borderTop: "var(--accent-line)" }}>
        <div className="section-inner" style={{ textAlign: "center" }}>
          <h2 className="section-title display">Partner With the Right Recycler</h2>
          <p className="section-body" style={{ margin: "0 auto 2rem" }}>
            Don't let compliance uncertainty or scale limitations constrain your EV roadmap. Talk to us.
          </p>
          <div style={{ display: "flex", gap: "1rem", justifyContent: "center", flexWrap: "wrap" }}>
            <a href="/#contact" className="btn-primary">Get in Touch</a>
            <Link to="/" className="btn-outline">← Back to Home</Link>
          </div>
        </div>
      </section>

    </main>
  );
}

export default WhyUsPage;